import { useState, useEffect, useCallback } from 'react';
import type { BlogArticle } from "@/lib/types";
import ImageUploader from '@/pages/admin/components/ImageUploader';
import {
  getArticles,
  saveArticle,
  deleteArticle,
  toggleFeatured,
} from "@/lib/api";

const CATEGORIES = ['Consejos', 'Recetas', 'Causas Sociales', 'Noticias', 'Tendencias'];

const emptyForm: Omit<BlogArticle, 'id' | 'created_at' | 'updated_at'> = {
  title: '',
  category: 'Noticias',
  excerpt: '',
  content: '',
  author: '',
  date: new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }),
  read_time: '5 min',
  image: '',
  featured: false,
};

const categoryColors: Record<string, string> = {
  Consejos: 'bg-[#00b4c8]/10 text-[#00b4c8]',
  Recetas: 'bg-[#fde68a]/50 text-[#b08000]',
  'Causas Sociales': 'bg-[#f9b8c8]/40 text-[#d4607a]',
  Noticias: 'bg-[#c9b8f0]/30 text-[#7c5cbf]',
  Tendencias: 'bg-[#f9b8c8]/30 text-[#d4607a]',
};

export default function AdminBlog() {
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('Todos');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<BlogArticle | null>(null);
  const [form, setForm] = useState<Omit<BlogArticle, 'id' | 'created_at' | 'updated_at'>>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const fetchArticles = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getArticles();

      data.sort(
        (a: BlogArticle, b: BlogArticle) =>
          new Date(b.created_at).getTime() -
          new Date(a.created_at).getTime()
      );

      setArticles(data);
    } catch (err) {
      console.error(err);
    }

  setLoading(false);
}, []);
  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const filtered = articles.filter((a) => {
    const matchSearch =
      a.title.toLowerCase().includes(search.toLowerCase()) ||
      a.author.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'Todos' || a.category === filterCat;
    return matchSearch && matchCat;
  });

  const openCreate = () => {
    setEditingArticle(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (article: BlogArticle) => {
    setEditingArticle(article);
    setForm({
      title: article.title,
      category: article.category,
      excerpt: article.excerpt,
      content: article.content,
      author: article.author,
      date: article.date,
      read_time: article.read_time,
      image: article.image,
      featured: article.featured,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;

    setSaving(true);

    try {
      await saveArticle({
        ...form,
        id: editingArticle?.id ?? 0,
      });

      showToast(
        editingArticle
          ? "Artículo actualizado correctamente"
          : "Artículo creado correctamente"
      );

      setModalOpen(false);

      fetchArticles();
    } catch (err) {
      console.error(err);

      showToast(
        editingArticle
          ? "Error al actualizar el artículo"
          : "Error al crear el artículo"
      );
    }

    setSaving(false);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteArticle(id);

      showToast("Artículo eliminado");

      fetchArticles();
    } catch (err) {
      console.error(err);

      showToast("Error al eliminar el artículo");
    }

    setDeleteConfirm(null);
  };

  const handleToggleFeatured = async (article: BlogArticle) => {
    try {
      if (article.featured) {
        return;
      }

      await toggleFeatured(article.id);

      showToast("Artículo destacado actualizado");

      fetchArticles();
    } catch (err) {
      console.error(err);

      showToast("Error al actualizar el destacado");
    }
  };

  return (
    <div className="space-y-5">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[#2d1a0e] text-white text-sm px-5 py-3 rounded-xl">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-emerald-400"></i>
            {toast}
          </span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#2d1a0e]">
            Gestión del Blog
          </h1>
          <p className="text-sm text-[#8a6a55]">{articles.length} artículos publicados</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-[#d4607a] hover:bg-[#b84d65] text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-sm"></i>
          </span>
          Nuevo Artículo
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#8a6a55]">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar artículo o autor..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-[#c9b8f0]/40 bg-white text-sm text-[#2d1a0e] focus:outline-none focus:border-[#d4607a] transition-colors"
          />
        </div>
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 bg-white text-sm text-[#2d1a0e] focus:outline-none focus:border-[#d4607a] cursor-pointer"
        >
          <option value="Todos">Todas las categorías</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[#8a6a55] text-sm">
          <i className="ri-loader-4-line text-2xl animate-spin text-[#d4607a] block mx-auto mb-2"></i>
          Cargando artículos...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((article) => (
            <div key={article.id} className="bg-white rounded-xl border border-[#f0e6d8] overflow-hidden">
              <div className="relative h-36">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
                {article.featured && (
                  <span className="absolute top-2 left-2 bg-amber-400 text-[#2d1a0e] text-xs px-2 py-0.5 rounded-full font-bold">
                    DESTACADO
                  </span>
                )}
                <span className={`absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full font-medium ${categoryColors[article.category] ?? 'bg-gray-100 text-gray-600'}`}>
                  {article.category}
                </span>
              </div>
              <div className="px-4 py-3">
                <h4 className="font-semibold text-[#2d1a0e] text-sm leading-tight mb-1 line-clamp-2">
                  {article.title}
                </h4>
                <p className="text-xs text-[#8a6a55] mb-2 line-clamp-2">{article.excerpt}</p>
                <div className="flex items-center gap-2 text-xs text-[#b09080] mb-3">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-user-line text-xs"></i>
                  </span>
                  <span className="truncate">{article.author}</span>
                  <span>·</span>
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.read_time}</span>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handleToggleFeatured(article)}
                    className={`text-xs px-2.5 py-1 rounded-full font-medium cursor-pointer transition-colors whitespace-nowrap ${
                      article.featured
                        ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {article.featured ? '★ Destacado' : '☆ Destacar'}
                  </button>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEdit(article)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f8f4ff] text-[#6b5b6e] hover:text-[#00b4c8] transition-colors cursor-pointer"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(article.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#5a3e2b] hover:text-red-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full py-12 text-center text-[#8a6a55] text-sm">
              No se encontraron artículos
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-[#c9b8f0]/20 flex items-center justify-between">
              <h3 className="font-bold text-[#2d1a0e]">
                {editingArticle ? 'Editar Artículo' : 'Nuevo Artículo'}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f8f4ff] cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-[#6b5b6e] text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                  Título *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#d4607a] transition-colors"
                  placeholder="Título del artículo"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                    Categoría
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#d4607a] cursor-pointer"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                    Autor
                  </label>
                  <input
                    type="text"
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#d4607a] transition-colors"
                    placeholder="Nombre del autor"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                    Fecha
                  </label>
                  <input
                    type="text"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#d4607a] transition-colors"
                    placeholder="Ej: 28 Abr 2026"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                    Tiempo de Lectura
                  </label>
                  <input
                    type="text"
                    value={form.read_time}
                    onChange={(e) => setForm({ ...form, read_time: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#d4607a] transition-colors"
                    placeholder="Ej: 5 min"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                  Extracto / Resumen
                </label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  rows={2}
                  maxLength={500}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#d4607a] transition-colors resize-none"
                  placeholder="Breve descripción del artículo..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                  Contenido
                </label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={5}
                  maxLength={500}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#d4607a] transition-colors resize-none"
                  placeholder="Contenido completo del artículo..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                  Imagen del Artículo
                </label>
                <ImageUploader
                  value={form.image}
                  onChange={(base64) => setForm({ ...form, image: base64 })}
                  accentColor="#d4607a"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, featured: !form.featured })}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${form.featured ? 'bg-[#d4607a]' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.featured ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
                <span className="text-sm text-[#5a3e2b]">Artículo destacado</span>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#c9b8f0]/20 flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#6b5b6e] hover:bg-[#f8f4ff] transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={!form.title.trim() || saving}
                className="px-4 py-2 rounded-lg bg-[#d4607a] hover:bg-[#b84d65] text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                {editingArticle ? 'Guardar Cambios' : 'Publicar Artículo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-center font-bold text-[#2d1a0e] mb-2">¿Eliminar artículo?</h3>
            <p className="text-center text-sm text-[#8a6a55] mb-5">Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-[#e8d5c4] text-sm text-[#5a3e2b] hover:bg-[#fdf6f0] transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
